import {
  ApiArtifact,
  ApiBookArtifact,
  ApiImageArtifact,
  ApiInteraction,
  ApiInteractionArtifact,
  ApiThreeSixtyArtifact,
  ApiThreeSixtyImage,
  ApiThreeSixtyInteraction,
} from "../../models/api";
import {
  Artifact,
  BookArtifact,
  ImageArtifact,
  Interaction,
  InteractionArtifact,
  ThreeSixtyArtifact,
  ThreeSixtyImage,
  ThreeSixtyInteraction,
} from "../../models/client";
import { toImageConfig } from "./image";
import { toPosition } from "./position";

export function toArtifact(
  apiArtifact: ApiArtifact,
  themeId: number | string
): Artifact {
  switch (apiArtifact.Type) {
    case "bookArtifact":
      return toBookArtifact(apiArtifact, themeId);
    case "interactiveArtifact":
      return toInteractionArtifact(apiArtifact, themeId);
    case "threeSixtyArtifact":
      return toThreeSixtyArtifact(apiArtifact, themeId);
    default:
      return toPhotoArtifact(apiArtifact, themeId);
  }
}

export function toPhotoArtifact(
  apiArtifact: ApiImageArtifact,
  themeId: number | string
): ImageArtifact {
  return {
    id: apiArtifact.Id,
    themeId,
    name: apiArtifact.Name,
    longName: apiArtifact.LongName,
    summary: apiArtifact.Summary,
    previewImg: apiArtifact.PreviewImg
      ? toImageConfig(apiArtifact.PreviewImg)
      : toImageConfig(apiArtifact.FullImg),
    type: apiArtifact.Type,
    fullImg: toImageConfig(apiArtifact.FullImg),
    helperText: "View exhibit",
  };
}

export function toBookArtifact(
  apiArtifact: ApiBookArtifact,
  themeId: number | string
): BookArtifact {
  return {
    id: apiArtifact.Id,
    themeId,
    name: apiArtifact.Name,
    longName: apiArtifact.LongName,
    summary: apiArtifact.Summary,
    previewImg: toImageConfig(apiArtifact.PreviewImg),
    type: apiArtifact.Type,
    pages: apiArtifact.Pages.map(toImageConfig),
    hasCover: apiArtifact.HasCover,
    height: apiArtifact.Height,
    width: apiArtifact.Width,
    helperText: "Look inside",
  };
}

export function toInteractionArtifact(
  apiArtifact: ApiInteractionArtifact,
  themeId: number | string
): InteractionArtifact {
  const baseArtifact = {
    id: apiArtifact.Id,
    themeId,
    name: apiArtifact.Name,
    longName: apiArtifact.LongName,
    summary: apiArtifact.Summary,
    previewImg: toImageConfig(apiArtifact.PreviewImg),
    type: apiArtifact.Type,
    imgStart: toImageConfig(apiArtifact.ImgStart),
    imgEnd: toImageConfig(apiArtifact.ImgEnd),
    helperText: "Look inside",
  };

  // if (apiArtifact.Interaction.TransitionType === "cycle") {
  //   return {
  //     ...baseArtifact,
  //     interaction: {
  //       transitionType: "cycle",
  //       images: apiArtifact.Interaction.Images.map(toImageConfig),
  //     },
  //   };
  // }
  return {
    ...baseArtifact,
    interaction: {
      transitionType: "default",
    },
  };
}

export function toThreeSixtyArtifact(
  apiArtifact: ApiThreeSixtyArtifact,
  themeId: number | string
): ThreeSixtyArtifact {
  return {
    id: apiArtifact.Id,
    themeId,
    name: apiArtifact.Name,
    longName: apiArtifact.LongName,
    summary: apiArtifact.Summary,
    previewImg: toImageConfig(apiArtifact.PreviewImg),
    type: apiArtifact.Type,
    threeSixtyImages: toThreeSixtyImages(
      apiArtifact.ThreeSixtyImages,
      apiArtifact.ThreeSixtyInteractions
    ),
    helperText: "Take a tour",
  };
}

export function toThreeSixtyImages(
  apiThreeSixtyImages: ApiThreeSixtyImage[],
  apiThreeSixtyInteractions: ApiThreeSixtyInteraction[]
): ThreeSixtyImage[] {
  return apiThreeSixtyImages.map((apiThreeSixtyImage) => {
    if (apiThreeSixtyImage.InteractionConfig) {
      const intId = apiThreeSixtyImage.InteractionConfig.InteractionId;
      const int = apiThreeSixtyInteractions.find(
        (interaction) => interaction.Id === intId
      );

      if (!int) {
        return {
          id: apiThreeSixtyImage.Id,
          image: toImageConfig(apiThreeSixtyImage.Image),
        };
      }

      return {
        id: apiThreeSixtyImage.Id,
        image: toImageConfig(apiThreeSixtyImage.Image),
        interaction: toThreeSixtyInteraction(
          apiThreeSixtyImage as Required<ApiThreeSixtyImage>,
          int
        ),
      };
    }
    return {
      id: apiThreeSixtyImage.Id,
      image: toImageConfig(apiThreeSixtyImage.Image),
    };
  });
}

export function toThreeSixtyInteraction(
  apiThreeSixtyImage: Required<ApiThreeSixtyImage>,
  apiInteraction: ApiThreeSixtyInteraction
): ThreeSixtyInteraction {
  if (apiInteraction.Image.Title === apiThreeSixtyImage.Image.Title) {
    return {
      id: apiThreeSixtyImage.Id,
      interaction: toInteraction(apiInteraction.Interaction),
      position: toPosition(apiThreeSixtyImage.InteractionConfig.Position),
      imgStart: toImageConfig(apiThreeSixtyImage.Image),
      imgEnd: toImageConfig(apiInteraction.Image),
      type: "interaction",
    };
  }

  return {
    id: apiThreeSixtyImage.Id,
    type: "link",
    position: toPosition(apiThreeSixtyImage.InteractionConfig.Position),
    imageId: apiInteraction.Image.Title,
    interaction: toInteraction(apiInteraction.Interaction),
    imgStart: toImageConfig(apiThreeSixtyImage.Image),
    imgEnd: toImageConfig(apiInteraction.Image),
  };
}

export function toInteraction(apiInteraction: ApiInteraction): Interaction {
  if (apiInteraction && apiInteraction.TransitionType === "cycle") {
    return {
      transitionType: "cycle",
      images: apiInteraction.Images.map(toImageConfig),
    };
  }
  return {
    transitionType: "default",
  };
}
